<template>
  <div class="appointment-filter">
    <div class="appointment-filter__chips">
      <template v-for="item in Object.entries(dayTime)" :key="item[0]">
        <Chip
          v-if="item[1]"
          :aria-label="`Filter entfernen: ${item[0].replace(/([A-Z])/g, ' $1')}`"
          :label="item[0].replace(/([A-Z])/g, ' $1')"
          @click="removeChip(item[0])"
        />
      </template>
    </div>
    <template v-if="isMobile">
      <button
        class="appointment-filter__button"
        @click="toggleFilter"
        aria-expanded="false"
        aria-controls="filter-dialog"
        aria-label="Filter öffnen"
      >
        <IconFilter aria-hidden="true" />Filter
      </button>
      <el-dialog
        v-model="showFilter"
        :show-close="false"
        :lock-scroll="true"
        custom-class="filter"
        id="filter-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="filter-dialog-title"
      >
        <AppointmentFilter
          :initialDayTime="dayTime"
          @saveDayTime="saveDayTime"
          @closeFilter="closeFilter"
        />
      </el-dialog>
    </template>
    <template v-else>
      <el-popover
        trigger="click"
        placement="bottom-end"
        width="320px"
        @after-enter="activate()"
        @before-leave="deactivate()"
      >
        <template #reference>
          <button
            class="appointment-filter__button"
            aria-haspopup="dialog"
            aria-expanded="false"
            aria-controls="filter-popover"
          >
            <IconFilter aria-hidden="true" />Filter
          </button>
        </template>
        <div
          id="filter-popover"
          ref="filterRef"
          role="dialog"
          aria-labelledby="filter-popover-title"
        >
          <AppointmentFilter :initialDayTime="dayTime" @saveDayTime="saveDayTime" />
        </div>
      </el-popover>
    </template>
  </div>
</template>

<script setup>
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import { computed, defineEmits, defineProps, ref } from 'vue'
import { useStore } from 'vuex'

import AppointmentFilter from '@/components/Booking/AppointmentFilter.vue'
import Chip from '@/components/Chip.vue'
import IconFilter from '@/components/icons/IconFilter.vue'

// Props
const props = defineProps({
  initialDayTime: {
    type: Object,
    required: true,
  },
})

// Emits
const emit = defineEmits(['update:dayTime'])

const showFilter = ref(false)
const filterRef = ref(null)
const { activate, deactivate } = useFocusTrap(filterRef, { allowOutsideClick: true })

// Store
const store = useStore()
const isMobile = computed(() => store.getters.isMobile)
const dayTime = computed(() => props.initialDayTime)

// Methods
const toggleFilter = () => {
  showFilter.value = !showFilter.value
}

const closeFilter = () => {
  showFilter.value = false
}

const saveDayTime = (newDayTime) => {
  emit('update:dayTime', newDayTime)
  closeFilter()
}

const removeChip = (dayTimeKey) => {
  const updatedDayTime = {
    ...dayTime.value,
    [dayTimeKey]: false,
  }
  emit('update:dayTime', updatedDayTime)
}
</script>

<style lang="scss">
.appointment-filter {
  display: flex;
  flex-grow: 1;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-width: calc(100% - 80px);
    overflow-x: auto;

    @media (max-width: $breakpoint-max-mobile) {
      margin-right: 0;
      order: 3;
      max-width: 100%;
      width: 100%;
    }
  }

  &__button {
    @include small-text;
    display: flex;
    align-items: center;
    background-color: $color-grey-light;
    border-radius: 16px;
    border: none;
    cursor: pointer;
    font-weight: $font-weight-medium;
    gap: 4px;
    padding: 4px 8px;

    &:hover,
    &:focus-visible {
      background-color: $color-grey-dark;
      outline: 2px solid $color-info-primary;
      outline-offset: 2px;
    }
  }
}
</style>
